import type { TaskDetail as TaskRecord } from '@dyspatch/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowUpRight, ScrollText } from 'lucide-react';
import {
  Empty,
  EventLog,
  PropertyGrid,
  RepoChip,
  SizeChip,
  TaskKey,
} from '@/components/common/bits';
import { TaskStateChip } from '@/components/common/StatusIcon';
import { Button } from '@/components/ui/button';
import { PausedChip } from '@/features/actions/PauseActions';
import { TaskNotes } from '@/features/tasks/TaskNotes';
import {
  TaskAttempts,
  TaskDecision,
  TaskDependencyChips,
  TaskProgressList,
  TaskVerbsPopover,
  taskDecision,
  taskFactRows,
  useTaskShape,
} from '@/features/tasks/TaskParts';
import { api } from '@/lib/api';
import { duration } from '@/lib/format';
import { shortKey } from '@/lib/keys';
import { hubKeys } from '@/lib/query';

/**
 * A task at a glance, in the 400px pane the board pops out at the side.
 *
 * It used to be the only place a task existed: `/tasks/:id` rendered this and
 * nothing else, so the widest thing on the page was this column and a task's
 * spec had nowhere to be drawn at all. A task is the unit a person judges, so
 * it has a page of its own now (`routes/task.tsx`, issue #61) and this went
 * back to being what its width can honestly carry: what state the task is in,
 * whose move it is, and the verb for that move.
 *
 * Everything here is built from the same parts the page is (`TaskParts.tsx`),
 * so the two cannot drift, and the header links to the page for the rest: the
 * spec, the acceptance criteria, the timings, and room to read the journal in
 * something other than a 400px column.
 *
 * Issue #100 rebuilt the pane itself: it used to be four bordered bands - a
 * label/value table, then "Cancel task" as bare red text in its own band,
 * then "Pause" in a second, then "Change this task" in a third - wrapped
 * around three controls and a table. It is one surface now (`SplitPane`
 * already draws that surface around `{peek}`), its regions take their space
 * from `dy-stack` instead of a border each, and the three verbs share the one
 * popover trigger and rows the task page already opened for this
 * (`TaskVerbsPopover`, issue #89) rather than a fourth telling of "a menu of
 * the things you can do to this".
 */
export function TaskDetail({ taskId }: { taskId: string }) {
  const detail = useQuery({
    queryKey: hubKeys.task(taskId),
    queryFn: () => api.task(taskId),
  });

  if (detail.isPending) return <Empty>Loading…</Empty>;
  if (detail.isError) {
    return <Empty>{detail.error instanceof Error ? detail.error.message : 'not found'}</Empty>;
  }
  return <TaskPane detail={detail.data} />;
}

/**
 * The pane itself, once there is a task to draw.
 *
 * Split out because `useTaskShape` is a hook and the loading and error cases
 * above return before there is a task to hand it.
 */
function TaskPane({ detail }: { detail: TaskRecord }) {
  const { task, events, notes, attempts } = detail;
  const shape = useTaskShape(task, events);
  const decision = taskDecision(task, shape);
  // The bare `cancel` kind has no card of its own here either (issue #89):
  // it is the admin's only verb on something in flight, and it moves into
  // the popover with pause and change rather than standing as a fourth band.
  // `gate`/`failed`/`stuck`/`waiting` keep their pinned card - each already
  // explains the state it is a decision about, in the same colours the page
  // uses for it.
  const pinnedDecision = decision && decision !== 'cancel' ? decision : null;
  // State, size, repo and the job are chips and links in the header now
  // (issue #100), not rows in the table below, so they are not asked twice.
  // Dependencies are the header's own chip line for the same reason.
  const facts = taskFactRows(task, shape).filter(
    (row) => row.label !== 'State' && row.label !== 'Job' && row.label !== 'After',
  );

  return (
    <div className="dy-stack py-3">
      <div className="px-gutter">
        <div className="flex flex-wrap items-center gap-2">
          <TaskKey briefId={task.briefId}>{task.key}</TaskKey>
          <TaskStateChip status={task.status} />
          {shape.isGate ? (
            <span className="text-fg-3 text-2xs">{duration(shape.waited)} waiting</span>
          ) : null}
          <SizeChip size={task.size} />
          <RepoChip repo={task.repo} />
          {task.pausedAt ? <PausedChip /> : null}
          {/* The one control for pause, resume, change and cancel, wherever
              this task carries them - the same trigger and the same rows the
              task page opens (`TaskVerbsPopover`), so the pane does not
              invent a second reading of "what can be done to this task". */}
          <div className="ml-auto shrink-0">
            <TaskVerbsPopover task={task} shape={shape} />
          </div>
        </div>
        <h3 className="mt-1.5 text-md font-semibold">{task.title}</h3>
        {/* The two ways out of this pane, as buttons.
            They were a grey 11px "Open the task →" and the word "Job:" with
            the job's title underlined after it - two pieces of prose that
            happened to be clickable, at the smallest size the console has, on
            the one line of the pane that is entirely navigation. Nothing said
            they were controls except the cursor, and the job's own button was
            whatever length the job's title happened to be.
            Now both are buttons with a hairline and an icon, at the size every
            other control in the console is, and the job's title truncates
            inside its own rather than setting the row's width. */}
        <div className="mt-2 flex min-w-0 flex-wrap items-center gap-1.5">
          <Button asChild variant="outline" size="xs">
            <Link to="/tasks/$taskId" params={{ taskId: task.id }}>
              <ArrowUpRight aria-hidden />
              Open the task
            </Link>
          </Button>
          <Button asChild variant="ghost" size="xs" className="min-w-0 max-w-full">
            <Link to="/jobs/$briefId" params={{ briefId: task.briefId }}>
              <ScrollText aria-hidden />
              <span className="text-fg-3 shrink-0">Job</span>
              <span className="min-w-0 truncate">
                {shape.brief?.title ?? shortKey(task.briefId)}
              </span>
            </Link>
          </Button>
        </div>
        {task.dependsOn.length ? (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-fg-3 text-2xs">Depends on</span>
            <TaskDependencyChips task={task} shape={shape} />
          </div>
        ) : null}
      </div>

      {pinnedDecision ? <TaskDecision task={task} shape={shape} /> : null}

      {/* The same property band the page opens with, in the one column this
          width can honestly hold: at 400px a second column would put two
          six-character values beside each other and wrap every one that is
          longer. The icons come with the rows, so a reader moving between the
          pane and the page is looking for the same shapes in both. */}
      <PropertyGrid rows={facts} columns={1} />
      <TaskProgressList task={task} bordered={false} />
      <TaskNotes task={task} notes={notes} />
      <TaskAttempts attempts={attempts} bordered={false} />

      <section>
        <h4 className="dy-eyebrow px-gutter pb-1.5">Events</h4>
        <EventLog events={events} />
      </section>
    </div>
  );
}
